/**
 * TotNghiepOcrPageV2 — nhập điểm tốt nghiệp bằng AI từ ảnh biên bản chấm thi.
 *
 * Ba điều đã định hình màn hình này:
 *
 *  1. Không có gì được ghi cho tới khi một người bấm xác nhận. AI đọc chữ viết
 *     tay, và một con số sai đi vào hồ sơ tốt nghiệp thì không ai phát hiện cho
 *     tới lúc có người tới hỏi.
 *  2. Việc đọc chạy ở job nền, nên trang phải poll — một xấp biên bản của cả
 *     khoá mất vài phút, quá mọi ngưỡng timeout.
 *  3. Dòng chưa khớp được học viên phải CHẶN, không phải cảnh báo. Ghi điểm vào
 *     nhầm hồ sơ là hỏng thứ không sửa lại được bằng mắt.
 *
 * Sao khuôn `InvoiceOcrPage.jsx` — cùng một hình dạng việc.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form, ProgressBar, Row, Spinner, Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import totNghiepApi from '../../services/totNghiep';
import AdminLogoutButton from '../../components/AdminLogoutButton';

const LOAI_NHAN = {
  hinh: 'Sa hình',
  chuChi: 'Chữ chi',
  duong: 'Đường',
  daoTao: 'Báo cáo đào tạo',
};

/** Nhãn + màu + lời giải thích cho từng trạng thái khớp học viên. */
const KHOP = {
  matched: { text: 'Đã khớp', bg: 'success', hint: 'Tìm thấy đúng một học viên' },
  ambiguous: { text: 'Cần chọn', bg: 'warning', hint: 'Nhiều người cùng khớp, hoặc thiếu ngày sinh — phải chọn tay' },
  no_match: { text: 'Không thấy', bg: 'danger', hint: 'Không tìm thấy học viên nào; sửa lại họ tên hoặc ngày sinh' },
};

const fmtNgay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (s || '');
};

export default function TotNghiepOcrPageV2() {
  const [files, setFiles] = useState([]);
  const [khoaHoc, setKhoaHoc] = useState('');
  const [pct, setPct] = useState(0);
  const [dangUp, setDangUp] = useState(false);
  const [batches, setBatches] = useState([]);
  const [current, setCurrent] = useState(null);
  const [dangGhi, setDangGhi] = useState(false);
  const inputRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try {
      const res = await totNghiepApi.listBatches({ limit: 30 });
      setBatches(res.data?.docs || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    }
  }, []);

  const loadBatch = useCallback(async (id) => {
    try {
      const res = await totNghiepApi.getBatch(id);
      setCurrent(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // Chỉ poll khi đang đọc. Một tab bị quên mở mà cứ gọi API mãi là thứ phải
  // tránh — hết đọc là dừng ngay.
  useEffect(() => {
    const id = current?.batch?._id;
    if (!id || current?.batch?.status !== 'extracting') return undefined;
    const t = setInterval(() => loadBatch(id), 4000);
    return () => clearInterval(t);
  }, [current?.batch?._id, current?.batch?.status, loadBatch]);

  const onPick = (e) => {
    const ds = [...(e.target.files || [])];
    const xau = ds.filter((f) => !/\.(jpe?g|png|webp|pdf)$/i.test(f.name));
    if (xau.length) {
      toast.error(`Chỉ nhận ảnh hoặc PDF. Bỏ qua: ${xau.map((f) => f.name).join(', ')}`);
    }
    setFiles(ds.filter((f) => /\.(jpe?g|png|webp|pdf)$/i.test(f.name)));
  };

  const onUpload = async () => {
    if (!files.length || dangUp) return;
    setDangUp(true);
    setPct(0);
    try {
      const res = await totNghiepApi.createBatch(files, {
        khoaHoc,
        onUploadProgress: (ev) => {
          if (ev.total) setPct(Math.round((ev.loaded * 100) / ev.total));
        },
      });
      toast.success('Đã nhận file, đang đọc…');
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      await loadBatches();
      if (res.data?._id) await loadBatch(res.data._id);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setDangUp(false);
      setPct(0);
    }
  };

  const suaItem = async (item, body) => {
    try {
      await totNghiepApi.updateItem(item._id, body);
      await loadBatch(current.batch._id);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    }
  };

  // useMemo: `current?.items || []` tạo mảng mới mỗi lần render, làm useMemo
  // của `sanSang` bên dưới chạy lại vô ích mỗi lần.
  const items = useMemo(() => current?.items || [], [current]);

  /**
   * Vị từ này phải TRÙNG KHÍT luật ở server (`commitBatch`). Lệch một điều kiện
   * thì nút bấm nói dối: hiện "Ghi 12 biên bản" rồi ghi được 9.
   */
  const gheDuoc = (it) => it.status === 'extracted'
    && it.reviewed
    && it.matchStatus === 'matched'
    && !!it.driving
    && !!it.resolved?.loai;

  const sanSang = useMemo(() => items.filter(gheDuoc), [items]);
  const soLoi = items.filter((i) => i.status === 'failed').length;

  const onCommit = async () => {
    if (!sanSang.length || dangGhi) return;
    if (!window.confirm(
      `Ghi ${sanSang.length} biên bản vào hệ thống?\n\n`
      + 'Điểm của những học viên này sẽ được cập nhật.',
    )) return;
    setDangGhi(true);
    try {
      const res = await totNghiepApi.commitBatch(current.batch._id);
      const r = res.data || {};
      toast.success(`Đã ghi ${r.ghi} biên bản${r.boQua ? `, bỏ qua ${r.boQua}` : ''}`);
      await loadBatch(current.batch._id);
      await loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setDangGhi(false);
    }
  };

  return (
    <Container fluid className='p-3'>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <h5 className='mb-0'>Nhập điểm tốt nghiệp bằng AI</h5>
        <AdminLogoutButton size='sm' />
      </div>

      <Card className='mb-3'>
        <Card.Body>
          <Row className='g-2 align-items-end'>
            <Col md={6}>
              <Form.Label className='fw-semibold mb-1'>Ảnh biên bản chấm thi (.jpg / .png / .pdf)</Form.Label>
              <Form.Control ref={inputRef} type='file' multiple accept='image/*,.pdf' onChange={onPick} disabled={dangUp} />
              <Form.Text className='text-muted'>
                Một tờ giấy có thể chứa hai biên bản — hệ thống tách thành hai dòng riêng.
              </Form.Text>
            </Col>
            <Col md={3}>
              <Form.Label className='fw-semibold mb-1'>Khóa học (tuỳ chọn)</Form.Label>
              <Form.Control
                value={khoaHoc}
                onChange={(e) => setKhoaHoc(e.target.value)}
                placeholder='vd C1-K24'
                disabled={dangUp}
              />
              <Form.Text className='text-muted'>
                Chọn khóa thì ít dòng phải chọn tay hơn hẳn.
              </Form.Text>
            </Col>
            <Col md={3}>
              <Button onClick={onUpload} disabled={!files.length || dangUp} className='w-100'>
                {dangUp ? <><Spinner size='sm' animation='border' className='me-2' />Đang tải lên…</> : `Tải lên ${files.length || ''}`}
              </Button>
            </Col>
          </Row>
          {dangUp && pct > 0 && <ProgressBar now={pct} label={`${pct}%`} className='mt-2' />}
        </Card.Body>
      </Card>

      <Row>
        <Col md={3}>
          <Card>
            <Card.Header className='py-2 small fw-semibold'>Các lần nhập</Card.Header>
            <div style={{ maxHeight: '64vh', overflow: 'auto' }}>
              {!batches.length && <div className='text-muted small p-3'>Chưa có lần nhập nào.</div>}
              {batches.map((b) => (
                <button
                  key={b._id}
                  type='button'
                  className={`btn btn-link d-block text-start w-100 px-3 py-2 text-decoration-none ${current?.batch?._id === b._id ? 'fw-bold' : ''}`}
                  onClick={() => loadBatch(b._id)}
                >
                  <div className='small'>{new Date(b.createdAt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}</div>
                  <div className='text-muted small'>
                    {b.itemCount || 0} dòng · {b.status}
                    {b.khoaHocLoc ? ` · ${b.khoaHocLoc}` : ''}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </Col>

        <Col md={9}>
          {!current && <Alert variant='light' className='border'>Chọn một lần nhập bên trái để xem.</Alert>}

          {current && (
            <Card>
              <Card.Header className='d-flex justify-content-between align-items-center flex-wrap gap-2 py-2'>
                <div className='small'>
                  <strong>{current.batch.itemCount || 0}</strong> biên bản ·{' '}
                  trạng thái <strong>{current.batch.status}</strong>
                  {current.batch.status === 'extracting' && (
                    <Spinner size='sm' animation='border' className='ms-2' />
                  )}
                  {soLoi > 0 && <span className='text-danger ms-2'>{soLoi} dòng lỗi</span>}
                </div>
                <div className='d-flex gap-2'>
                  {soLoi > 0 && (
                    <Button
                      size='sm'
                      variant='outline-secondary'
                      onClick={async () => {
                        // Đọc lại là tốn tiền API thật — hỏi trước.
                        if (!window.confirm('Đọc lại các dòng lỗi? Việc này gọi lại AI và tốn phí.')) return;
                        await totNghiepApi.retryBatch(current.batch._id);
                        toast.info('Đang đọc lại…');
                        loadBatch(current.batch._id);
                      }}
                    >
                      Đọc lại dòng lỗi
                    </Button>
                  )}
                  <Button
                    size='sm'
                    variant='success'
                    disabled={!sanSang.length || dangGhi || current.batch.status === 'committed'}
                    onClick={onCommit}
                  >
                    {dangGhi ? <><Spinner size='sm' animation='border' className='me-2' />Đang ghi…</> : `Ghi ${sanSang.length} biên bản`}
                  </Button>
                </div>
              </Card.Header>

              <div style={{ maxHeight: '62vh', overflow: 'auto' }}>
                <Table size='sm' hover className='align-middle small mb-0'>
                  <thead className='table-light position-sticky top-0'>
                    <tr>
                      <th>Loại</th>
                      <th>Họ tên</th>
                      <th>Ngày sinh</th>
                      <th className='text-end'>Điểm</th>
                      <th>Kết luận</th>
                      <th>Học viên</th>
                      <th>Duyệt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const v = it.resolved || {};
                      const k = KHOP[it.matchStatus] || KHOP.no_match;
                      const daSua = (f) => it.corrections && Object.prototype.hasOwnProperty.call(it.corrections, f);
                      const oSua = (f) => (daSua(f) ? 'fw-semibold text-primary' : '');
                      if (it.status === 'failed') {
                        return (
                          <tr key={it._id} className='table-danger'>
                            <td colSpan={7} className='small'>
                              {it.originalName} — {it.error}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={it._id} className={it.matchStatus !== 'matched' ? 'table-warning' : ''}>
                          <td>{LOAI_NHAN[v.loai] || <span className='text-danger'>không rõ</span>}</td>
                          <td className={oSua('hoTen')}>
                            <Form.Control
                              size='sm'
                              defaultValue={v.hoTen || ''}
                              onBlur={(e) => {
                                if (e.target.value !== (v.hoTen || '')) suaItem(it, { corrections: { hoTen: e.target.value } });
                              }}
                            />
                          </td>
                          <td className={oSua('ngaySinh')}>{fmtNgay(v.ngaySinh) || <span className='text-danger'>thiếu</span>}</td>
                          <td className={`text-end ${oSua('soDiemDat')}`}>
                            {v.soDiemDat === null || v.soDiemDat === undefined
                              ? <span className='text-muted'>—</span> : v.soDiemDat}
                          </td>
                          <td>
                            {v.ketLuan === 'dat' && <Badge bg='success'>Đạt</Badge>}
                            {v.ketLuan === 'khongDat' && <Badge bg='danger'>Không đạt</Badge>}
                            {!v.ketLuan && <span className='text-muted'>—</span>}
                          </td>
                          <td>
                            <Badge bg={k.bg} text={k.bg === 'warning' ? 'dark' : undefined} title={k.hint}>{k.text}</Badge>
                            {it.matchStatus === 'ambiguous' && it.candidates?.length > 0 && (
                              <Form.Select
                                size='sm'
                                className='mt-1'
                                value={it.driving || ''}
                                onChange={(e) => suaItem(it, { driving: e.target.value })}
                              >
                                <option value=''>— chọn học viên —</option>
                                {it.candidates.map((c) => (
                                  <option key={c.driving} value={c.driving}>
                                    {c.name} · {fmtNgay(c.dob) || 'không rõ ngày sinh'} · {c.registrationCode}
                                  </option>
                                ))}
                              </Form.Select>
                            )}
                          </td>
                          <td>
                            <Form.Check
                              type='checkbox'
                              checked={!!it.reviewed}
                              disabled={it.status === 'committed'}
                              onChange={(e) => suaItem(it, { reviewed: e.target.checked })}
                              label={it.status === 'committed' ? 'đã ghi' : ''}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
}
